if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          arrows: this.querySelectorAll('.product-gallery-arrow'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        /*
         * MAIN GALLERY ARROWS
         *
         * These directly move the existing product gallery.
         */
        this.elements.arrows.forEach((arrow) => {
          arrow.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const direction = arrow.classList.contains('product-gallery-arrow--next')
              ? 1
              : -1;

            this.changeSlide(direction);
          });
        });

        /*
         * Keep Shopify's existing slideChanged functionality.
         */
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), 500)
          );
        }

        /*
         * Thumbnail functionality.
         */
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');

            if (!button) return;

            button.addEventListener(
              'click',
              this.setActiveMedia.bind(
                this,
                mediaToSwitch.dataset.target,
                false
              )
            );
          });

        if (
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      /*
       * Move the main product gallery one slide forward/backward.
       */
      changeSlide(direction) {
        if (!this.elements.viewer) return;

        const slides = Array.from(
          this.elements.viewer.querySelectorAll(
            '#Slider-Gallery-' + this.closest('media-gallery').id.replace('MediaGallery-', '')
          )
        );

        let slider = this.elements.viewer.slider;

        /*
         * Fallback: find the actual product slides directly.
         */
        const sliderElement =
          this.elements.viewer.querySelector('[id^="Slider-Gallery"]');

        if (!sliderElement) return;

        const slideItems = Array.from(
          sliderElement.querySelectorAll('.product__media-item')
        );

        if (!slideItems.length) return;

        /*
         * Find the currently active slide.
         */
        let currentIndex = slideItems.findIndex((slide) =>
          slide.classList.contains('is-active')
        );

        /*
         * If there isn't an active class, determine the slide
         * closest to the current scroll position.
         */
        if (currentIndex === -1) {
          const currentScroll = sliderElement.scrollLeft;

          currentIndex = slideItems.reduce(
            (closestIndex, slide, index) => {
              const closestSlide = slideItems[closestIndex];

              return Math.abs(slide.offsetLeft - currentScroll) <
                Math.abs(closestSlide.offsetLeft - currentScroll)
                ? index
                : closestIndex;
            },
            0
          );
        }

        let newIndex = currentIndex + direction;

        /*
         * Loop from the beginning/end.
         */
        if (newIndex >= slideItems.length) {
          newIndex = 0;
        }

        if (newIndex < 0) {
          newIndex = slideItems.length - 1;
        }

        const targetSlide = slideItems[newIndex];

        if (!targetSlide) return;

        /*
         * Update active class.
         */
        slideItems.forEach((slide) => {
          slide.classList.remove('is-active');
        });

        targetSlide.classList.add('is-active');

        /*
         * Scroll the existing Shopify slider.
         */
        sliderElement.scrollTo({
          left: targetSlide.offsetLeft,
          behavior: 'smooth',
        });

        /*
         * Update thumbnails.
         */
        if (this.elements.thumbnails) {
          const mediaId = targetSlide.dataset.mediaId;

          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

          if (thumbnail) {
            this.setActiveThumbnail(thumbnail);
          }
        }

        /*
         * Play videos/media if necessary.
         */
        this.playActiveMedia(targetSlide);
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) {
          return;
        }

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          if (activeMediaRect.top > -0.5) return;

          const top =
            activeMediaRect.top + window.scrollY;

          window.scrollTo({
            top: top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        if (!activeThumbnail) return;

        this.setActiveThumbnail(activeThumbnail);

        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) =>
            element.removeAttribute('aria-current')
          );

        const button = thumbnail.querySelector('button');

        if (button) {
          button.setAttribute('aria-current', true);
        }

        if (
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) =>
            slide.setAttribute(
              'role',
              'presentation'
            )
        );
      }
    }
  );
}